import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { Icon } from '../components/Icon';
import { portalApi, ApiError } from '../api/client';
import type { IdeaResponse } from '../api/types';
import { computeProductKPIs } from '../lib/financial/product-net-value';

type Filter = 'all' | 'drafts' | 'submitted';

export function DashboardPage() {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<IdeaResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('Your organisation');
  const [filter, setFilter] = useState<Filter>('all');
  const [totalSavings, setTotalSavings] = useState(0);

  useEffect(() => {
    let cancelled = false;

    portalApi.me()
      .then((res) => { if (!cancelled) setTenantName(res.tenant_name); })
      .catch(() => {});

    portalApi
      .listIdeas()
      .then(async (res) => {
        if (cancelled) return;
        setIdeas(res.ideas);

        // Compute 36-month projected savings for submitted monetary ideas using
        // the financial library (CAPEX amortization + recurring intervals).
        const submitted = res.ideas.filter((i) => !i.is_draft && i.is_monetary);
        const savingsTotals = await Promise.all(
          submitted.map(async (idea) => {
            try {
              const [costs, savings] = await Promise.all([
                portalApi.listCosts(idea.id),
                portalApi.listSavings(idea.id),
              ]);
              const kpis = computeProductKPIs(
                { one_time: costs.one_time as unknown as Record<string, unknown>[], recurring: costs.recurring as unknown as Record<string, unknown>[] },
                { one_time: savings.one_time as unknown as Record<string, unknown>[], recurring: savings.recurring as unknown as Record<string, unknown>[] },
              );
              return kpis.totalValue;
            } catch {
              // Fall back to the raw monetary_value if line items are unavailable.
              return idea.monetary_value ?? 0;
            }
          }),
        );
        if (!cancelled) {
          setTotalSavings(savingsTotals.reduce((sum, v) => sum + v, 0));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (!(err instanceof ApiError && err.status === 401)) setIdeas([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const submittedIdeas = ideas.filter((i) => !i.is_draft);

  const filteredIdeas =
    filter === 'drafts'
      ? ideas.filter((i) => i.is_draft)
      : filter === 'submitted'
      ? submittedIdeas
      : ideas;

  return (
    <div className="min-h-screen bg-bg">
      <Hero
        eyebrow={tenantName}
        title="Your ideas dashboard"
        subtitle="Manage and track your submitted use-case ideas."
      />
      <div className="mx-auto max-w-[1060px] px-10 pb-28 pt-7">
        <div className="mb-6 flex gap-3">
          <StatCard value={String(submittedIdeas.length)} label="Ideas submitted" />
          <StatCard value={formatCurrency(totalSavings)} label="Est. savings" />
        </div>

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-[1.05rem] font-bold">My ideas</h2>
            <div className="flex gap-1">
              {(['all', 'drafts', 'submitted'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    filter === f
                      ? 'bg-accent text-white'
                      : 'bg-accent-light text-accent hover:bg-accent/20'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'drafts' ? 'Drafts' : 'Submitted'}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/ideas/new')}
            className="inline-flex items-center gap-1.5 rounded-[9px] bg-accent px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-hover"
          >
            <Icon name="plus" size={13} /> New idea
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-muted">Loading your ideas…</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(265px,1fr))] gap-3">
            {filteredIdeas.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} onEdit={() => navigate(`/ideas/${idea.id}/edit`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex-1 rounded-card border border-line bg-white px-5 py-4">
      <div className="text-[1.85rem] font-extrabold tracking-tight text-accent">{value}</div>
      <div className="mt-0.5 text-xs text-muted">{label}</div>
    </div>
  );
}

function IdeaCard({ idea, onEdit }: { idea: IdeaResponse; onEdit: () => void }) {
  const monetary = idea.is_monetary;
  return (
    <button
      type="button"
      onClick={onEdit}
      className="group relative rounded-card border border-line bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-[0_6px_20px_rgba(0,78,146,0.1)]"
    >
      <div className="mb-2.5 flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1.5 rounded-[5px] bg-accent-light px-2 py-1 text-[0.7rem] font-bold uppercase tracking-wide text-accent">
          <Icon name={monetary ? 'moneyXs' : 'chartXs'} size={11} />
          {monetary ? 'Monetary' : 'Strategic'}
        </span>
        {idea.is_draft && (
          <span className="inline-flex items-center rounded-[5px] border border-warn-mid bg-warn-light px-2 py-1 text-[0.7rem] font-bold uppercase tracking-wide text-warn">
            Draft
          </span>
        )}
      </div>
      <div className="mb-1.5 text-sm font-bold leading-snug">{idea.name}</div>
      <div className="mb-3.5 line-clamp-2 text-[0.79rem] leading-relaxed text-muted">
        {idea.description}
      </div>
      <div className="flex items-center justify-end">
        <span className="flex items-center gap-1 text-[0.77rem] font-semibold text-accent">
          {idea.is_draft ? 'Continue' : 'Edit'} <Icon name="chevronR" size={12} />
        </span>
      </div>
    </button>
  );
}

function formatCurrency(amount: number): string {
  if (amount >= 1000) return `€${Math.round(amount / 1000)}k`;
  return `€${Math.round(amount)}`;
}
